import { FC, useMemo } from "react";
import { LineChart as RechartsLineChart, XAxis, YAxis, Line } from "recharts";
import { GridLegacy as Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardFooter } from "./common/card";
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
} from "./common/chart-config";
import { CSVDownloader } from "../fileDownload/csv-downloader";
import { IChartOptions, isChartOptions } from "./common/chart-options";
import { IChartProps } from "./common/types";
import { useUserParams } from "../../context/user-params-context";
import { formatLabel } from "./common/format-helpers";
import { ChartInfoTooltip } from "./common/card-info-tooltip";
import { useChartData } from "../../hooks/useChartData";
import { useReleaseManagement, ReleaseFlag } from "@/utility/feature-management";
import { resolveChartTitle } from "./common/resolve-chart-title";
import { usePreferences } from "../../utility/usePreferences";
import { capitalise } from "../../utility/utils";

interface QueryResultItem {
  name: string;
  [key: string]: string | number;
}

interface LineOptions {
  fill?: string;
  dataKey?: string;
}

interface ILineChartOptions extends IChartOptions {
  line?: Record<string, LineOptions>;
}

export const LineChart: FC<IChartProps> = ({ uuid }) => {
  const { t } = useTranslation();
  const { userParams, parameterTypes } = useUserParams();
  const { distancePerHour, distanceUnitText, distanceUnitSingular } = usePreferences();

  const { data, isLoading } = useChartData<QueryResultItem[], ILineChartOptions>(
    uuid,
    parameterTypes,
    userParams,
    { validateOptions: isChartOptions }
  );

  const releaseRefactorChartTitle = useReleaseManagement(
    ReleaseFlag.releaseRefactorChartTitle
  );

  const chartOptions = data?.options;
  const chartData = data?.data || [];
  const chartName = data?.name;
  const chartTranslationKey = data?.translationKey ?? undefined;

  const chartConfig: ChartConfig = Object.entries(chartOptions?.line || {}).reduce(
    (acc, [key, lineOptions]) => {
      acc[key] = {
        label: key,
        color: lineOptions.fill || `var(--color-${key})`,
      };
      return acc;
    },
    {} as ChartConfig
  );

  const computedTitle = useMemo(() => {
    return resolveChartTitle({
      t,
      name: chartName ?? undefined,
      translationKey: chartTranslationKey,
      featureEnabled: releaseRefactorChartTitle,
      defaultText: chartName ?? undefined, // fallback to original name
      i18nParams: {
        distancePerHour,
        distanceUnit: capitalise(distanceUnitText),
        distanceUnitSingular: capitalise(distanceUnitSingular),
        timeFormat: "hh:mm",
      },
    });
  }, [
    t,
    chartName,
    chartTranslationKey,
    releaseRefactorChartTitle,
    distancePerHour,
    distanceUnitText,
    distanceUnitSingular,
  ]);

  return (
    <Grid item xs={12} md={8} lg={6}>
      <Card
        style={{
          animation: isLoading ? "pulse 2s linear infinite" : undefined,
        }}
      >
        {chartOptions?.description && (
          <ChartInfoTooltip description={chartOptions.description} />
        )}
        <CardContent>
          <ChartContainer config={chartConfig}>
            <RechartsLineChart data={chartData}>
              <XAxis
                dataKey={chartOptions?.xAxis?.dataKey || "name"}
                label={{
                  value: formatLabel(userParams, chartOptions?.xAxis?.label),
                  position: "insideBottom",
                  offset: 0,
                }}
                interval={chartOptions?.xAxis?.interval}
                tickFormatter={(value) =>
                  formatLabel(userParams, {
                    ...chartOptions?.xAxis?.tick?.label,
                    value,
                  })
                }
              />
              <YAxis
                allowDuplicatedCategory={false}
                label={{
                  value: formatLabel(userParams, chartOptions?.yAxis?.label),
                  angle: chartOptions?.yAxis?.angle || -90,
                  position: "insideLeft",
                  offset: 0,
                }}
                interval={chartOptions?.yAxis?.interval}
                tickFormatter={(value) =>
                  formatLabel(userParams, {
                    ...chartOptions?.yAxis?.tick?.label,
                    value,
                  })
                }
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    heading={chartOptions?.tooltip?.heading}
                    nameKey={chartOptions?.tooltip?.nameKey}
                    labelFormatter={(value) =>
                      formatLabel(userParams, {
                        ...chartOptions?.tooltip?.label,
                        value,
                      })
                    }
                  />
                }
              />
              {Object.entries(chartOptions?.line || {}).map(([key, lineOptions]) => {
                const dataKey = lineOptions.dataKey;
                const stroke = lineOptions.fill || `var(--color-${key})`;
                return (
                  <Line
                    key={key}
                    type="monotone"
                    dataKey={dataKey}
                    stroke={stroke}
                    strokeWidth={2}
                  />
                );
              })}
              {chartOptions?.showLegend && (
                <ChartLegend
                  verticalAlign="top"
                  align="right"
                  layout="vertical"
                  formatter={(value) => (
                    <span style={{ color: "black" }}>{value}</span>
                  )}
                />
              )}
            </RechartsLineChart>
          </ChartContainer>
        </CardContent>
        <CardFooter>
          <Typography variant="subtitle1">
            {isLoading ? t("general.loading") : computedTitle}
          </Typography>
          {chartOptions?.enableCSVDownload && (
            <CSVDownloader data={chartData} filename={chartName || ""} />
          )}
        </CardFooter>
      </Card>
    </Grid>
  );
};

export default LineChart;
